package mapsearch


import apiclient.geoobjects.LatLon
import apiclient.geoobjects.MarkerColor
import apiclient.geoobjects.distanceTo
import apiclient.mapsearch.MapFeature
import apiclient.mapsearch.MapMarker
import apiclient.mapsearch.MarkerCluster
import apiclient.mapsearch.SearchSpec
import apiclient.mapsearch.UserMarker
import apiclient.util.JsSimpleCache
import auth.CurrentWorkspaceStore
import com.jillesvangurp.geo.tiles.Tile
import com.jillesvangurp.geo.tiles.parentTiles
import data.objects.ActiveObjectStore
import dev.fritz2.core.RootStore
import dev.fritz2.core.SimpleHandler
import kotlin.math.max
import kotlin.time.Duration.Companion.milliseconds
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.combine
import kotlinx.coroutines.flow.debounce
import kotlinx.coroutines.flow.distinctUntilChanged
import kotlinx.coroutines.flow.filter
import kotlinx.coroutines.flow.filterNotNull
import kotlinx.coroutines.flow.map
import map.MapStateStore
import maplibreGL.FormationMap
import maplibreGL.Marker
import maplibreGL.toLngLat
import model.MapState
import mu.KotlinLogging
import utils.NULL_ISLAND
import utils.resolveColor

private val log = KotlinLogging.logger("ActiveMapTilesStore")

data class MapFeatureHolder(val path: String, val m: MapFeature)

data class MapTileData(val resultsHolder: MapMarkerTileSearchResultsHolder, val clusterMarkers: List<Marker>)

/**
 * Whenever this changes, the map should be updated to reflect what's in the store.
 */
class ActiveMapTilesStore(
    val formationMap: FormationMap,
    private val mapTileCache: MapTileCache,
    private val searchSpecStore: SearchSpecStore,
    val mapStateStore: MapStateStore,
    val currentWorkspaceStore: CurrentWorkspaceStore,
    private val mapRenderOverrides: MapRenderOverrides,
    private val clusterService: ClusterService,
    private val activeObjectStore: ActiveObjectStore
) : RootStore<List<MapTileData>>(listOf(), Job()) {

    // DONOT add support for other types of content here (buildings, geojson, whatever), handle that separately
    // this is just for keeping maptile markers in sync with the search spec and mapstate
    fun startMapTileSync() {
        log.info { "STARTING NEW MAP" }
        searchSpecStore.data handledBy { searchSpec ->
            try {// whenever the search spec changes, clear the cache and re-render the mapstate
                mapTileCache.clear()
                mapStateStore.current?.let { state ->
//                    log.info { "SearchSpec triggered with Zl: ${state.zoom} (${state.zoom.toInt()})" }
                    updateTilesToMap(state)
                }
            } catch (e: Exception) {
                log.error(e) { "Error handling new searchSpec $searchSpec" }
            }
        }

        mapStateStore.data.filterNotNull()
            // don't re-render the map all the time
            .debounce(100.milliseconds)
            .distinctUntilChanged() handledBy { newState ->
            try {
                // when editing an object with geometry, give some time
                // to update the new object position in lookUpStore,
                // before trigger search
                delay(100)
//                log.info { "MapState triggered with Zl: ${newState.zoom} (${newState.zoom.toInt()})" }
                updateTilesToMap(newState)
            } catch (e: Exception) {
                log.error(e) { "Error handling mapState $newState" }
            }
        }

        combine(
            mapRenderOverrides.pathToolActive.data,
            mapRenderOverrides.pathToolHighlightedObjectIds.data,
//            activeObjectStore.map(GeoObjectDetails.L.id).data,
//        ) { a, ids, ao -> Triple(a, ids, ao) } handledBy { (pathToolActive, trackedObjIds, activeObjId) ->
        ) { a, ids -> Pair(a, ids) } handledBy { (pathToolActive, trackedObjIds) ->
//            console.log("MAP RENDER OVERRIDES triggered updateTilesToMap")
            mapTileCache.clear()
            clusterService.excludeFromClusterFilter = if (pathToolActive) { feature ->
                feature is MapMarker && feature.id in trackedObjIds
            } else { feature ->
                feature is MapMarker && feature.id == activeObjectStore.current.id
            }

            mapStateStore.current?.let { state ->
                updateTilesToMap(
                    newState = state,
                    forceFullUpdate = true,
                )
            }
        }

        currentWorkspaceStore.data.filterNotNull().map { workspace ->
            (listOf(workspace.groupId) + workspace.sharedLayerDefinitions?.map { it.layerId }.orEmpty()).distinct()
        }.filter { it.isNotEmpty() } handledBy { workspaceIds ->
            searchSpecStore.update(
                searchSpecStore.current?.copy(
                    ownerIds = workspaceIds,
                ) ?: SearchSpec(
                    ownerIds = workspaceIds,
                    objectTypes = mapSearchObjectTypes,
                ),
            )
        }
    }

    /**
     * Simply triggers re-rendering of all the map markers using the current MapState
     */
    val refreshTilesOnMap = SimpleHandler<Unit> { data, _ ->
        data handledBy {
            delay(200)
            updateTilesToMap()
        }
    }

    private suspend fun updateTilesToMap(
        newState: MapState? = mapStateStore.current,
        forceFullUpdate: Boolean = false,
    ) {
        val map = formationMap.map
        if (map == null || newState == null) {
            log.error { "cannot update because map or mapState is null" }
            return
        }
        searchSpecStore.current?.let { searchSpec ->
            val newTiles = mapTileCache.fetchTiles(newState, searchSpec).associateBy { it.mapMarkerTileSearchResults.tilePath }.toMutableMap()

            val newActiveMaptiles = mutableListOf<MapTileData>()

            val oldTiles = current.associateBy { mapTileData ->
                mapTileData.resultsHolder.mapMarkerTileSearchResults.tilePath
            }
            oldTiles.forEach { (tilePath, mapTileData) ->
                val (cachedHolder, clusterMarkers) = mapTileData

                if (forceFullUpdate) {
//                    clusterMarkers.forEach { it.remove() }
                } else {
                    val newTile = newTiles[tilePath]
                    if (newTile != null) {
                        if (newTile == cachedHolder) {
                            // unmodified so don't re-add the markers
                            newTiles.remove(tilePath)
                            newActiveMaptiles.add(MapTileData(cachedHolder, clusterMarkers))
                        } else {
                            // newTile has different content hash so re-add all markers
//                            clusterMarkers.forEach { it.remove() }
                        }
                    } else {
                        // off screen, so remove
//                        clusterMarkers.forEach { it.remove() }
                    }
                }
            }

            val flattened = newTiles.flatMap { (_, holder) ->
                holder.mapMarkerTileSearchResults.clusters.flatMap { cm ->
                    if (cm.amount > 0) {
                        // split out markers initially and re-cluster as needed
                        listOf(cm.copy(markers = null)) + cm.markers.orEmpty()
                    } else {
                        cm.markers.orEmpty()
                    }.map { feature ->
                        MapFeatureHolder(holder.mapMarkerTileSearchResults.tilePath, feature)
                    }
                }
            }
//            log.info { "flattened ${flattened.size}" }
            val clusteredByTile = flattened.groupBy { it.path }

            newTiles.forEach { (_, holder) ->
                newActiveMaptiles.add(
                    MapTileData(
                        resultsHolder = holder,
                        clusterMarkers = run {
                            val ht = Tile.fromPath(holder.mapMarkerTileSearchResults.tilePath)
                            val cms = clusteredByTile[ht.path]?.map { it.m }.orEmpty()
                            val mapLibreMarkers = cms.mapNotNull { feature ->
                                when (feature) {
                                    is MapMarker -> {
                                        val mapLibreMarker = MarkerGenerators.createMapLibreMarker(feature)
                                        mapLibreMarker
                                    }

                                    is MarkerCluster -> {
                                        val mapLibreMarker = MarkerGenerators.createClusterMarker(feature)
                                        mapLibreMarker
                                    }

                                    else -> null
                                }
                            }
                            mapLibreMarkers
                        },
                    ),
                )
            }

            val flattenedMarkers: MutableMap<String, List<MapFeature>> = mutableMapOf()
            val clusterMarkers: MutableMap<String, List<MapFeature>> = mutableMapOf()

            // Get and Apply included zoneIds and generalMarkerIds to filter the separate SearchSpecs:
            // Distinguish between GlobalSearch and MapSearch leading the results
            // list of ids => GlobalSearch is active and filtering -> only show objects included in search results, if empty show none
            // null => MapSearch -> show all objects
            val zoneIds = mutableSetOf<String>()
            val generalMarkerIds = mutableSetOf<String>()

            newActiveMaptiles.map { it.resultsHolder }.forEach { cachedHolder ->
                cachedHolder.mapMarkerTileSearchResults.clusters.forEach { cm ->
                    val markersInCluster = cm.markers?.filterIsInstance<MapMarker>().orEmpty().mapNotNull { feature ->
                        when {
                            isGeneralMarker(feature.id) -> {
                                generalMarkerIds.add(feature.id)
                                null
                            }

                            isZone(feature.id) -> {
                                zoneIds.add(feature.id)
                                null
                            }

                            isBuilding(feature.id) || isUser(feature.id) -> {
                                null
                            }

                            else -> feature
                        }
                    }
                    flattenedMarkers[cachedHolder.mapMarkerTileSearchResults.tilePath] = markersInCluster
                    if (cm.amount > 0) {
//                        if (cm.amount.toInt() == 1) {
//                            console.log("Cluster WITH only 1!", cm.markers?.map { (it as? MapMarker)?.title })
//                        }
                        // remove markers from clusters
                        clusterMarkers[cachedHolder.mapMarkerTileSearchResults.tilePath] = listOf(
                            cm.copy(markers = null),
                        )
                    }
                }
            }

            setIncludedZones(
                if (searchSpec.isActive()) {
                    zoneIds
                } else null,
            )
            setIncludedGeneralMarkers(
                if (searchSpec.isActive()) {
                    generalMarkerIds
                } else null,
            )

            clusterService.setFeaturesFromMapSearch(flattenedMarkers, clusterMarkers)

            update(newActiveMaptiles)
        }
    }

    fun addMarkersToTiles(
        tileCache: JsSimpleCache<String, MapMarkerTileSearchResultsHolder>,
        newMarkers: List<MapMarker>,
    ) {
        // find all required tiles for the new zoneId @ latLon
        val tilesAtMaxZoom = newMarkers.map { marker -> marker.position.tile(Tile.MAX_ZOOM) }
        val requiredTiles = tilesAtMaxZoom.flatMap { tileAtMaxZoom -> (tileAtMaxZoom.parentTiles() + tileAtMaxZoom) }

        // Update the list
        val updatedList = current.map { mapTileData ->
            val (holder, markers) = mapTileData
            val updatedClusters = holder.mapMarkerTileSearchResults.clusters.map { cluster ->
                if (cluster.tilePath in requiredTiles.map { it.path }) {
                    cluster.copy(markers = (newMarkers + cluster.markers.orEmpty()).distinct())
                } else cluster
            }

            val updatedResults = holder.mapMarkerTileSearchResults.copy(clusters = updatedClusters)
            val updatedHolder = holder.copy(mapMarkerTileSearchResults = updatedResults)
            val newMapLibreMarkers = newMarkers.map { marker ->
                val mapMarker = MarkerGenerators.createMapLibreMarker(marker)
                mapMarker.setLngLat(marker.position.toLngLat())
                mapMarker
            }

            mapTileData.copy(resultsHolder = updatedHolder, clusterMarkers = markers + newMapLibreMarkers)
        }

        // Update the cache
        val updatedCache = tileCache.entries().map { (key, holder) ->
            val updatedClusters = holder.mapMarkerTileSearchResults.clusters.map { cluster ->
                if (cluster.tilePath in requiredTiles.map { it.path }) {
                    cluster.copy(markers = (newMarkers + cluster.markers.orEmpty()).distinct())
                } else cluster
            }

            val updatedResults = holder.mapMarkerTileSearchResults.copy(clusters = updatedClusters)
            val updatedHolder = holder.copy(mapMarkerTileSearchResults = updatedResults)

            key to updatedHolder
        }
        tileCache.clear()
        tileCache.putAll(updatedCache)

        update(updatedList)
//        refreshTilesOnMap(Unit)
    }

    fun updateMapMarkersInTiles(
        tileCache: JsSimpleCache<String, MapMarkerTileSearchResultsHolder>,
        newMarkers: List<MapMarker>,
    ) {

        // Create a lookup map for quick access to new markers by ID
        val newMarkersMap = newMarkers.associateBy { it.id }.toMutableMap()

//        console.log(
//            "UPDATE called updateMapMarkersInTiles (Markers) -> ",
//            newMarkersMap.values.map { "${it.title}: ${it.position.toLngLat()}, ${it.color}" }.toString(),
//        )

        // Update the list
        val updatedList = current.map { mapTileData ->
            val (holder, markers) = mapTileData
            val updatedClusters = holder.mapMarkerTileSearchResults.clusters.map { cluster ->
                val updatedFeatures = cluster.markers?.map { feature ->
                    if (feature is MapMarker && newMarkersMap.containsKey(feature.id)) {
                        newMarkersMap[feature.id]!! // Replace old MapMarker with new one
                    } else {
                        feature
                    }
                }
                cluster.copy(markers = updatedFeatures)
            }

            val updatedResults = holder.mapMarkerTileSearchResults.copy(clusters = updatedClusters)
            val updatedHolder = holder.copy(mapMarkerTileSearchResults = updatedResults)

            val updatedMarkers = markers.mapNotNull { marker ->
                (marker.asDynamic()._element?.id as? String)?.let { id ->
                    newMarkersMap[id]?.let { newMarker ->
                        // create new one, set new position
                        val newMapLibreMarker = MarkerGenerators.createMapLibreMarker(newMarker)
                        newMapLibreMarker.setLngLat(newMarker.position.toLngLat()) // Assuming setLngLat modifies Marker
                        newMapLibreMarker
                    } ?: marker
                }
            }

            mapTileData.copy(resultsHolder = updatedHolder, clusterMarkers = updatedMarkers)
        }

        // Update the cache
        val updatedCache = tileCache.entries().map { (key, holder) ->
            val updatedClusters = holder.mapMarkerTileSearchResults.clusters.map { cluster ->
                val updatedFeatures = cluster.markers?.map { feature ->
                    if (feature is MapMarker && newMarkersMap.containsKey(feature.id)) {
                        newMarkersMap[feature.id]!! // Replace old MapMarker with new one and remove from map
                    } else {
                        feature
                    }
                }
                cluster.copy(markers = updatedFeatures)
            }

            val updatedResults = holder.mapMarkerTileSearchResults.copy(clusters = updatedClusters)
            val updatedHolder = holder.copy(mapMarkerTileSearchResults = updatedResults)

            key to updatedHolder
        }

        tileCache.clear()
        tileCache.putAll(updatedCache)

        update(updatedList)
//        refreshTilesOnMap(Unit)
    }

    fun deleteMapMarkersInTiles(
//        tileCache: JsSimpleCache<String, MapMarkerTileSearchResultsHolder>,
        deleteMarkerIds: List<String>,
    ) {
        // Update the list
        val updatedList = current.map { mapTileData ->
            val (holder, markers) = mapTileData
            val updatedClusters = holder.mapMarkerTileSearchResults.clusters.map { cluster ->
                val updatedFeatures = cluster.markers?.mapNotNull { feature ->
                    if (feature is MapMarker && feature.id in deleteMarkerIds) {
                        null // Remove old MapMarker
                    } else {
                        feature
                    }
                }
                cluster.copy(markers = updatedFeatures)
            }

            val updatedResults = holder.mapMarkerTileSearchResults.copy(clusters = updatedClusters)
            val updatedHolder = holder.copy(mapMarkerTileSearchResults = updatedResults)

            val updatedMarkers = markers.mapNotNull { marker ->
                (marker.asDynamic()._element?.id as? String)?.let { id ->
                    if (id in deleteMarkerIds) {
                        null
                    } else marker
                }
            }

            mapTileData.copy(resultsHolder = updatedHolder, clusterMarkers = updatedMarkers)
        }

        // Update the cache
        val updatedCache = mapTileCache.cache.entries().map { (key, holder) ->
            val updatedClusters = holder.mapMarkerTileSearchResults.clusters.map { cluster ->
                val updatedFeatures = cluster.markers?.mapNotNull { feature ->
                    if (feature is MapMarker && feature.id in deleteMarkerIds) {
                        null // Remove old MapMarker
                    } else {
                        feature
                    }
                }
                cluster.copy(markers = updatedFeatures)
            }

            val updatedResults = holder.mapMarkerTileSearchResults.copy(clusters = updatedClusters)
            val updatedHolder = holder.copy(mapMarkerTileSearchResults = updatedResults)

            key to updatedHolder
        }
        mapTileCache.cache.clear()
        mapTileCache.cache.putAll(updatedCache)

        update(updatedList)
        refreshTilesOnMap(Unit)
    }
}


val MapFeature.weight
    get() = when (this) {
        is MapMarker -> 1
        is MarkerCluster -> fetched + amount
        is UserMarker -> 0
    }

fun avgPosition(left: MapFeature, right: MapFeature): LatLon {
    val leftPos = left.position
    val rightPos = right.position
    return if (leftPos != null && rightPos != null) {
        val lw = left.weight
        val rw = right.weight
        val totalWeight = lw + rw
        val lat = leftPos.lat * lw + rightPos.lat * rw
        val lon = leftPos.lon * lw + rightPos.lon * rw

        LatLon(lat / totalWeight, lon / totalWeight)
    } else {
        (leftPos ?: rightPos) ?: LatLon.Companion.NULL_ISLAND
    }
}

fun combineMapFeaturesToCluster(m1: MapFeature, m2: MapFeature): MapFeature {

    val markers: List<MapFeature>
    val position = avgPosition(m1, m2)
    val amount = when (m1) {
        is MapMarker -> 1
        is MarkerCluster -> max(m1.fetched.toLong(), m1.amount)
        else -> 0
    } + when (m2) {
        is MapMarker -> 1
        is MarkerCluster -> max(m2.fetched.toLong(), m2.amount)
        else -> 0
    }
    val colors: Map<String, Int>
    val tilePath: String

    when {
        m1 is MarkerCluster && m2 is MapMarker -> {
            markers = m1.markers.orEmpty() + m2

            colors = m1.colorBreakDown.toMutableMap()
            val c2 = m2.color ?: resolveColor(MarkerColor.Default)
//            if (c2 != null) {
            colors[c2] = colors.getOrPut(c2) { 0 } + 1
//            }

            tilePath = m1.tilePath
        }

        m1 is MarkerCluster && m2 is MarkerCluster -> {
            markers = m1.markers.orEmpty() + m2.markers.orEmpty()
            colors = m1.colorBreakDown.toMutableMap()
            m2.colorBreakDown.forEach { (color, count) ->
                colors[color] = colors.getOrPut(color) { 0 } + count
            }

            tilePath = m1.tilePath.ifBlank { m2.tilePath }
        }

        m1 is MapMarker && m2 is MarkerCluster -> {
            markers = m2.markers.orEmpty() + m1
            colors = m2.colorBreakDown.toMutableMap()
            val c1 = m1.color ?: resolveColor(MarkerColor.Default)
//            if (c1 != null) {
            colors[c1] = colors.getOrPut(c1) { 0 } + 1
//            }

            tilePath = m2.tilePath
        }

        m1 is MapMarker && m2 is MapMarker -> {
            markers = listOf(m1, m2)
            colors = mutableMapOf()
            val c1 = m1.color ?: resolveColor(MarkerColor.Default)
            val c2 = m2.color ?: resolveColor(MarkerColor.Default)
//            if (c1 != null) {
            colors[c1] = colors.getOrPut(c1) { 0 } + 1
//            }
//            if (c2 != null) {
            colors[c2] = colors.getOrPut(c2) { 0 } + 1
//            }

            tilePath = ""
        }

        else -> error("should not happen")
    }

    return MarkerCluster(
        tilePath = tilePath,
        amount = amount,
        position = position,
        colorBreakDown = colors,
        markers = markers,
    )
}


val MapFeature.position
    get() = when (this) {
        is MapMarker -> position
        is MarkerCluster -> position
        else -> null
    }

/**
 * Returns distance in meters between the two markers.
 */
fun MapFeature.distance(other: MapFeature): Double {
    val thisPos = position
    val otherPos = other.position
    return if (this === other) {
        0.0
    } else if (thisPos != null && otherPos != null) {
        thisPos.distanceTo(otherPos)
    } else {
        0.0
    }
}

val MapFeature.fetched
    get() = when (this) {
        is MapMarker -> 1
        is MarkerCluster -> this.markers?.size ?: 0
        else -> 0
    }

